import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
  Typography,
  Tooltip,
  Chip,
} from "@mui/material";
import ArgonButton from "components/ArgonButton";
import ActionModal from "components/Modals/ActionModal";
import { getSubCategories } from "services/subCategoriesService";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Image } from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DataTable from "examples/Tables/DataTable";
import { deleteSubCategory } from "services/subCategoriesService";
import SubCategoryModal from "./subCategoryModal";
import {
  getCategoryDisplayName,
  hasTranslations,
  getCategoryTranslations,
} from "../../../../utils/categoryUtils";
import { SUPPORTED_LANGUAGES } from "../../../../constants/languages";
function ViewSubCategoriesModal({ open, onClose, category, onSuccess }) {
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteSubCategoryId, setDeleteSubCategoryId] = useState(null);

  useEffect(() => {
    const fetchSubCategories = async () => {
      if (!category?._id) return;
      setLoading(true);
      try {
        const response = await getSubCategories(category._id);
        setSubCategories(response.data || []);
      } catch (err) {
        console.error("Failed to fetch subcategories", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSubCategories();
  }, [category]);

  const handleDeleteSubCategory = async (id) => {
    setDeleteSubCategoryId(id);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteSubCategory(deleteSubCategoryId);
      setSubCategories(
        subCategories.filter((subCat) => subCat._id !== deleteSubCategoryId)
      );
      onSuccess();
      setDeleteModal(false);
      setDeleteSubCategoryId(null);
    } catch (err) {
      console.error("Failed to delete sub-category", err);
      setDeleteModal(false);
      setDeleteSubCategoryId(null);
    }
  };

  const cancelDelete = () => {
    setDeleteModal(false);
    setDeleteSubCategoryId(null);
  };

  const handleEditSubCategory = (subCategory) => {
    setSelectedSubCategory(subCategory);
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setSelectedSubCategory(null);
  };

  const handleEditSuccess = () => {
    // Refresh the sub-categories list after successful edit
    const fetchSubCategories = async () => {
      if (!category?._id) return;
      setLoading(true);
      try {
        const response = await getSubCategories(category._id);
        setSubCategories(response.data || []);
      } catch (err) {
        console.error("Failed to fetch subcategories", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSubCategories();
    onSuccess();
  };

  // Helper function to render translations tooltip for subcategories
  const renderSubCategoryTranslationsTooltip = (subCategory) => {
    if (!hasTranslations(subCategory)) {
      return null;
    }

    const translations = getCategoryTranslations(subCategory);
    const languageNames = SUPPORTED_LANGUAGES.reduce((acc, lang) => {
      acc[lang.code] = lang.name;
      return acc;
    }, {});

    return (
      <Tooltip
        title={
          <Box sx={{ p: 1 }}>
            <ArgonTypography
              variant="body2"
              fontWeight="bold"
              mb={1}
              color="#2d3f50"
            >
              Available Languages:
            </ArgonTypography>
            {translations.map((translation) => (
              <Box key={translation.language} mb={0.5}>
                <ArgonTypography variant="body2" color="#2d3f50">
                  <strong>
                    {languageNames[translation.language] ||
                      translation.language}
                    :
                  </strong>{" "}
                  {translation.name}
                </ArgonTypography>
              </Box>
            ))}
          </Box>
        }
        arrow
        placement="top"
        sx={{
          "& .MuiTooltip-tooltip": {
            backgroundColor: "#ffffff",
            color: "#2d3f50",
            fontSize: "0.875rem",
            padding: "12px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            border: "1px solid #e0e0e0",
          },
          "& .MuiTooltip-arrow": {
            color: "#ffffff",
          },
        }}
      >
        <Box>
          <Chip
            label={`${translations.length} languages`}
            size="small"
            sx={{
              backgroundColor: "#e3f2fd",
              color: "#1976d2",
              fontWeight: "medium",
              fontSize: "0.75rem",
              ml: 1,
            }}
          />
        </Box>
      </Tooltip>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            overflow: "hidden",
          },
        }}
      >
        {loading ? (
          <ArgonBox
            display="flex"
            justifyContent="center"
            p={10}
            borderRadius={2}
          >
            <CircularProgress />
          </ArgonBox>
        ) : (
          <DialogContent
            sx={{
              bgcolor: "#fff",
              borderRadius: 2,
              p: 3,
              overflow: "auto",
              position: "relative",
              maxHeight: "calc(90vh - 120px)",
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                }}
              >
                <CircularProgress size={50} />
              </Box>
            )}

            <ArgonTypography
              variant="h4"
              align="center"
              sx={{ fontWeight: "bold" }}
            >
              Sub-Category
            </ArgonTypography>

            <ArgonBox display="flex" alignItems="center">
              <Box
                style={{
                  width: 50,
                  height: 50,
                  marginRight: 10,
                  borderRadius: 15,
                  border: "1px solid #e0e0e0",
                  backgroundColor: "#f2f2f2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {category?.icon ? (
                  <img
                    src={category.icon}
                    alt="icon"
                    style={{
                      borderRadius: 5,
                      width: "60%",
                      height: "60%",
                    }}
                  />
                ) : (
                  <Image fontSize="large" color="primary" />
                )}
              </Box>
              <ArgonBox>
                <ArgonTypography fontWeight="medium" variant="h5" mb={0.5}>
                  {getCategoryDisplayName(category)}
                </ArgonTypography>
                {hasTranslations(category) && (
                  <Chip
                    label={`${
                      getCategoryTranslations(category).length
                    } languages`}
                    size="small"
                    sx={{
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      fontWeight: "medium",
                      fontSize: "0.75rem",
                    }}
                  />
                )}
              </ArgonBox>
            </ArgonBox>

            <Box
              mt={2}
              style={{
                borderRadius: 10,
                boxShadow: "0 0 5px 1px rgba(18, 14, 14, 0.3)",
              }}
            >
              <DataTable
                table={{
                  columns: [
                    { Header: "Name", accessor: "name", align: "left" },
                    {
                      Header: "Actions",
                      accessor: "actions",
                      align: "center",
                    },
                  ],
                  rows: subCategories.map((subCat) => ({
                    name: (
                      <ArgonBox display="flex" alignItems="center">
                        <Typography
                          fontWeight="medium"
                          sx={{ fontSize: 16, color: "#172B4D" }}
                        >
                          {getCategoryDisplayName(subCat)}
                        </Typography>
                        {renderSubCategoryTranslationsTooltip(subCat)}
                      </ArgonBox>
                    ),
                    actions: (
                      <Box display="flex" justifyContent="center" gap={1}>
                        <Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSubCategory(subCat._id)}
                          >
                            <DeleteIcon fontSize="small" />
                            <Typography fontSize={12} fontWeight={700}>
                              Delete
                            </Typography>
                          </IconButton>
                        </Box>

                        <Box>
                          <IconButton
                            size="small"
                            sx={{ color: "#a36932" }}
                            onClick={() => handleEditSubCategory(subCat)}
                          >
                            <EditIcon fontSize="small" />
                            <Typography fontSize={12} fontWeight={700}>
                              Edit
                            </Typography>
                          </IconButton>
                        </Box>
                      </Box>
                    ),
                  })),
                }}
                entriesPerPage={false}
                canSearch={false}
                showTotalEntries={false}
                pagination={false}
                isLoading={loading}
              />
            </Box>

            <Box mt={3} textAlign="center">
              <ArgonButton
                variant="outlined"
                color="secondary"
                onClick={onClose}
                disabled={loading}
                sx={{
                  minWidth: 100,
                  backgroundColor: "#ffff",
                  color: "#2d3f50",
                  mt: 3,
                }}
              >
                Close
              </ArgonButton>
            </Box>
          </DialogContent>
        )}
      </Dialog>

      {/* Edit Sub-Category Modal */}
      {openEditModal && (
        <SubCategoryModal
          open={openEditModal}
          onClose={handleCloseEditModal}
          category={selectedSubCategory}
          onSuccess={handleEditSuccess}
        />
      )}
      {deleteModal && (
        <ActionModal
          open={deleteModal}
          handleClose={cancelDelete}
          title="Delete Sub-Category"
          detail="Are you sure you want to delete this sub-category? This action cannot be undone."
          handleYes={confirmDelete}
          handleNo={cancelDelete}
          color="#e53e3e"
        />
      )}
    </>
  );
}

export default ViewSubCategoriesModal;
